import { routeHistory } from '@nackle/biw-web-router';
import { Form } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import ExpandableAlert from '../../../components/errors/ExpandableAlert';
import { message } from 'antd';
import { TAB_ICONS, TAB_STATES } from './GenericTab';
import { getParam, setParam } from '../../hooks/useURLQueries';

export const useCreateEditGeneric = ( {
    id, idName,
    label,
    beforeSave,
    canSave: _canSave = true, canDelete: _canDelete = true, canPublish: _canPublish = true,
    editRoute, viewRoute,
    creationTabStates, creationInitialValues,
    createMutation, fetch, patchMutation, deleteMutation,
    formatDataToForm, formatFormToData
} ) => {
    
    const [ generic, setGeneric ] = useState();
    const [ messageApi, messageContext ] = message.useMessage();

    const [ form ] = Form.useForm();
    
    /**
     * The id prop comes from the route, and `routeHistory.push` updates it asynchronously, so it
     * is still undefined through the renders that follow a create. Every query hook normalizes its
     * item onto `.id`, so the loaded/created item is the authoritative source once it exists.
     */
    const itemId = id ?? generic?.id;
    const isCreating = !itemId;
    const isPublished = useMemo( () => !!generic?.isPublished, [ generic ] );
    
    const [ loading, setLoading ] = useState( true );
    const [ saving, setSaving ] = useState( false );
    const [ disabled, setDisabled ] = useState( false );
    const [ error, setError ] = useState();
    const [ activeTab, setActiveTab ] = useState();
    const [ validationError, setValidationError ] = useState();
    
    const [ initialValues, setInitialValues ] = useState( {} );
    const [ tabStates, setTabStates ] = useState( {} );

    const editTabState = useMemo( () => {
        const states = { ...creationTabStates };
        Object.keys( creationTabStates ).forEach( t => states[ t ] = [] );
        return states;
    }, [ creationTabStates ] );

    useEffect( () => {
        const urlTab = getParam( 'tab' );
        const tabNames = Object.keys( creationTabStates );
        const defaultTab = tabNames.includes( urlTab ) ? urlTab : tabNames[ 0 ];
        selectTab( defaultTab );
    }, [ creationTabStates ] );

    function selectTab( tabName ) {
        if ( tabStates[ tabName ]?.includes( TAB_STATES.LOCKED ) ) { return; }
        setParam( 'tab', tabName );
        setActiveTab( tabName );
    }

    useEffect( () => {
        if ( !isCreating ) {
            loadGeneric();
        } else {
            initializeGeneric();
        }
    }, [] );

    /** DATA MANIPULATION */

    const initializeGeneric = () => {
        setTabStates( creationTabStates );
        setInitialValues( creationInitialValues );
        form.setFieldsValue( creationInitialValues );
        setLoading( false );
    };

    function reset() {
        setTabStates( editTabState );
        setLoading( false );
    }

    function applyGeneric( item ) {
        const appliedItem = { ...generic, ...item }; //this application is not very smart, so manual updates are likey needed via softUpdate fn prior to this
        setGeneric( appliedItem );
        const formData = formatDataToForm( appliedItem, form );
        setInitialValues( formData );
        form.setFieldsValue( formData );
    }

    /** CRUD FUNCTIONS */

    async function createGeneric( input ) {
        try {
            const result = await createMutation( input );
            const newId = result.data[ idName ];
            messageApi.success( `Successfully created ${ label }!` );
            routeHistory.push( editRoute.buildPath( newId ) );
            const item = result.data;

            applyGeneric( item );
            reset();
        } catch ( e ) {
            console.error( e );
            setValidationError( e );
            setError( <ExpandableAlert message={ `Failed to create ${ label }` } error={ e } /> );
            setSaving( false );
        }
    }

    async function loadGeneric() {
        setLoading( true );
        setTabStates( editTabState );
        try {
            const result = await fetch( { id } );
            const item = result.data;
            applyGeneric( item );
        } catch ( e ) {
            console.error( e );
            setError( <ExpandableAlert message={ `Failed to load ${ label }` } error={ e } /> );
            setDisabled( true );
            setTabStates( tabs => Object.fromEntries( Object.entries( tabs ).map( ( [ k ] ) => [ k, [ TAB_STATES.LOCKED ] ] ) ) );
        } finally {
            setLoading( false );
        }
    }

    async function patchGeneric( input ) {

        try {
            const result = await patchMutation( input );
            messageApi.success( `Successfully saved ${ label }!` );
            const item = result.data;
            applyGeneric( item );
            reset();
        } catch ( e ) {
            console.error( e );
            setValidationError( e );
            setError( <ExpandableAlert message={ `Failed to save ${ label }` } error={ e } /> );
            setSaving( false );
        }
    }

    async function deleteGeneric( id ) {
        try {
            await deleteMutation( id );
        } catch ( e ) {
            console.error( e );
            setError( <ExpandableAlert message={ `Failed to delete ${ label }` } error={ e } /> );
            return;
        }

        await ( () => new Promise( res => setTimeout( res, 1000 ) ) )();

        routeHistory.push( viewRoute.buildPath() );
    }

    /**
     * Update the generic state variable without any requests
     * Used for manually updating variables that dont get overwritten from fetch content
     * @param {Object} item skinny object with only updated properties
     */
    function softUpdateGeneric( item ) {
        const data = { ...generic, ...item };
        setGeneric( data );
    }

    /** HANDLE CRUD ACTION FUNCTIONS */

    const handleSave = async () => {
        setSaving( true );
        setDisabled( true );
        setError();
        return await form.validateFields( Object.keys( initialValues ), { recursive: true } )
            .then( async vals => {
                let input = formatFormToData( vals );

                if ( beforeSave ) {
                    input = await beforeSave( { form, input, id: itemId } );
                }

                if ( isCreating ) {
                    await createGeneric( input );
                } else {
                    input[ idName ] = itemId;
                    await patchGeneric( input );
                }

                setDisabled( false );
                setSaving( false );
                return true;
            } )
            .catch( e => {
                console.error( e );
                setValidationError( e );
                setError( <ExpandableAlert message={ `Failed to save ${ label }` } error={ e } /> );
                setDisabled( false );
                setSaving( false );
                return false;
            } );

    };
    
    const handleCancel = async () => {
        setError();
        routeHistory.push( viewRoute.buildPath() );
    };

    const handleReset = async ( tab, fields ) => {
        if ( fields?.length > 0 ) {
            form.resetFields( fields );
            setTabStates( prev => ( { ...prev, [ tab ]: isCreating ? creationTabStates[ tab ] : editTabState[ tab ] } ) );
        }
    };

    const handleDelete = async () => {
        setError();
        setDisabled( true );
        await deleteGeneric( itemId );
    };

    const handlePublish = async ( bool ) => {
        setError();
        form.setFieldValue( 'isPublished', bool );
        const success = await handleSave();
        if ( !success ) {
            form.setFieldValue( 'isPublished', !bool );
        }
    };
    
    function onValidateTab( tabName, statuses ) {
        const newStates = { ...tabStates };
        newStates[ tabName ] = statuses;
        setTabStates( newStates );
    }

    function getTabIcon( tabStatus ) {
        return tabStatus?.includes( TAB_STATES.ERROR ) ? TAB_ICONS[ TAB_STATES.ERROR ] :
            tabStatus?.includes( TAB_STATES.REQUIRED ) ? TAB_ICONS[ TAB_STATES.REQUIRED ] :
                tabStatus?.includes( TAB_STATES.UNSAVED ) ? TAB_ICONS[ TAB_STATES.UNSAVED ] :
                    tabStatus?.includes( TAB_STATES.LOCKED ) ? TAB_ICONS[ TAB_STATES.LOCKED ] :
                        undefined;
    }

    const isDisabled = useMemo( () => {
        return disabled || tabStates[ activeTab ]?.includes( TAB_STATES.LOCKED );
    }, [ disabled, activeTab, tabStates ] );

    const [ canSave, saveTooltip ] = useMemo( () => {

        if ( !_canSave || isDisabled ) {
            return [ false, ];
        }

        const states = Object.values( tabStates ).flatMap( s => s );

        if ( states.some( t => [ TAB_STATES.ERROR, TAB_STATES.REQUIRED ].includes( t ) ) ) {
            return [ false, 'Some tabs require attention' ];
        }

        if ( states.every( t => ![ TAB_STATES.UNSAVED ].includes( t ) ) ) {
            return [ false, 'You have no unsaved changes' ];
        }

        return [ true, ];

    }, [ tabStates, _canSave, isDisabled ] );

    const canPublish = useMemo( () => {

        if ( !_canPublish || isDisabled ) {
            return false;
        }

        const states = Object.values( tabStates ).flatMap( s => s );

        if ( states.some( t => [ TAB_STATES.ERROR, TAB_STATES.REQUIRED ].includes( t ) ) ) {
            return false;
        }

        return true;
    }, [ tabStates, _canPublish, isDisabled ] );

    const canDelete = _canDelete && !isDisabled;

    const sidebarProps = useMemo( () => ( {
        form,
        item: generic,
        canSave, onSaveCb: handleSave, saveTooltip,
        canPublish, isPublished, onPublishCb: handlePublish,
        onResetCb: handleReset,
        onCancelCb: handleCancel,
        canDelete, onDeleteCb: handleDelete
    } ), [ form, generic, itemId, isCreating, initialValues, canSave, saveTooltip, canPublish, isPublished, canDelete ] );

    const tabProps = useMemo( () => ( {
        loading, saving,
        parentError: validationError,
        disabled: isDisabled,
        softUpdateGeneric,
        form,
        item: generic,
    } ), [ loading, validationError, disabled, form, generic, saving, softUpdateGeneric, activeTab ] );

    return useMemo( () => ( {
        isCreating, loading, error, setActiveTab: selectTab, activeTab, isPublished,
        form, initialValues,
        item: generic, itemId,
        getTabIcon, tabStates,
        onValidateTab,
        softUpdateGeneric,
        messageContext,
        sidebarProps, tabProps,
    } ), [ isCreating, loading, generic, itemId, initialValues, error, tabStates, setActiveTab, softUpdateGeneric, activeTab, isPublished, form, messageContext, sidebarProps, tabProps ] );
};